/**
 * Normalises icon-related fields from the three different data shapes used
 * across the app (NodeType store object, NodeDisplayProps, NodeInfo) into a
 * single flat { icon, headerImageUrl, category } record.
 */

export interface NodeIconResolved {
  /** Feather icon name e.g. "FiMessageSquare". Undefined → use category default. */
  icon?: string;
  /** Vendor SVG/PNG URL shown on the right side of a node header. */
  headerImageUrl?: string;
  /** Node category — used as fallback when icon is absent. */
  category?: string;
  /** Documentation URL — opens in a new browser tab. */
  helpUrl?: string;
}

/**
 * Source: NodeType / NodeTemplate objects in the Zustand store.
 * Top-level nodeType.icon takes precedence; settings.data…ui is the fallback
 * because the server may store icon/headerImageUrl only in that nested block.
 */
export function resolveFromNodeType(nodeType: any): NodeIconResolved {
  const ui: any =
    nodeType?.settings?.data?.processElement?.configuration?.designer?.ui ?? {};
  return {
    icon: (nodeType.icon as string | undefined) || (ui.icon as string | undefined),
    headerImageUrl: ui.headerImageUrl as string | undefined,
    category: nodeType.category as string | undefined,
    helpUrl: (nodeType.helpUrl as string | undefined) || (ui.helpUrl as string | undefined),
  };
}

/**
 * Source: NodeDisplayProps returned by getNodeDisplayProps() — used by canvas nodes.
 * Fields are already extracted from processElement.configuration.designer.ui.
 */
export function resolveFromDisplayProps(display: {
  icon?: string;
  headerImageUrl?: string;
  category?: string;
  helpUrl?: string;
}): NodeIconResolved {
  return {
    icon: display.icon,
    headerImageUrl: display.headerImageUrl,
    category: display.category,
    helpUrl: display.helpUrl,
  };
}

/**
 * Source: NodeInfo passed into ConnectorConfigDialog.
 */
export function resolveFromNodeInfo(nodeInfo?: {
  data?: { icon?: string; headerImageUrl?: string; category?: string; helpUrl?: string };
}): NodeIconResolved {
  return {
    icon: nodeInfo?.data?.icon,
    headerImageUrl: nodeInfo?.data?.headerImageUrl,
    category: nodeInfo?.data?.category,
    helpUrl: nodeInfo?.data?.helpUrl,
  };
}
